import { useState, useEffect } from "react";
import {
  Typography,
  Button,
  CircularProgress,
  makeStyles,
  Theme,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import {
  CheckCircle,
  Description as FileIcon,
  Warning,
  CloudUpload,
} from "@material-ui/icons";
import { useNotify, useDataProvider } from "react-admin";
import resourceNames from "../../../../common/data/resourceNames";
import { Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
// import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { useSignalR } from "../../../../context/SignalRContext";
import {
  SignalRTypeEnum,
  SignalRStatusEnum,
} from "../../../common/signal-r-popup/types";

type ImportFileData = {
  id: string;
  fileName: string;
  driverId: number | null;
  fileUrl: string;
  suggestedDriverFullName?: string;
  suggestedLicenseNumber?: string;
  suggestedState?: string;
  hasEvents?: boolean;
};

type ImportMapping = {
  fileId: string;
  selectedDriverId: number | undefined;
  allowOverwrite: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filesList: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: 8,
    },
    "&::-webkit-scrollbar-track": {
      background:
        theme.palette.type === "dark"
          ? theme.palette.grey[800]
          : theme.palette.grey[200],
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      background:
        theme.palette.type === "dark"
          ? theme.palette.grey[600]
          : theme.palette.grey[400],
      borderRadius: 4,
      "&:hover": {
        background:
          theme.palette.type === "dark"
            ? theme.palette.grey[500]
            : theme.palette.grey[600],
      },
    },
  },
  fileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[800]
        : theme.palette.background.paper,
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.grey[700]
          : theme.palette.background.paper,
    },
  },
  fileHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  overwriteSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(0, 1),
    color: theme.palette.text.secondary,
  },
  suggestedInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  driverSelect: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.grey[900]
          : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
    marginTop: "auto",
  },
  importButton: {
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: theme.spacing(2),
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  checkbox: {
    color: theme.palette.primary.main,
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
  },
}));

interface Props {
  importId?: string | null;
  onSuccess?: () => void;
}

const ELDFileImportForm = ({ importId, onSuccess }: Props) => {
  const classes = useStyles();
  const notify = useNotify();
  // const axiosInstance = useAxiosInstance();
  const signalR = useSignalR();
  const dataProvider = useDataProvider();

  const [uploadedFiles, setUploadedFiles] = useState<ImportFileData[]>([]);
  const [mappings, setMappings] = useState<Record<string, ImportMapping>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [isDriversLoading, setIsDriversLoading] = useState(false);

  useEffect(() => {
    const loadDrivers = async () => {
      setIsDriversLoading(true);
      try {
        const result = await dataProvider.getList(
          resourceNames.ADMIN_DRIVERS_VIEW,
          {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: "driverName", order: "ASC" },
            filter: { status: "Active" },
          }
        );
        setDrivers(Object.values(result.data));
      } catch (error) {
        console.error("Failed to load drivers:", error);
      } finally {
        setIsDriversLoading(false);
      }
    };

    loadDrivers();
  }, [dataProvider]);

  useEffect(() => {
    if (uploadedFiles.length > 0) return;

    setIsLoading(true);

    const timer = setTimeout(() => {
      const mockNames = [
        "John Smith",
        "Maria Garcia",
        "James Johnson",
        "Sarah Williams",
        "Michael Brown",
        "Jennifer Davis",
        "Robert Miller",
        "Lisa Wilson",
      ];
      const mockStates = ["CA", "TX", "FL", "NY", "IL", "PA", "OH", "GA"];

      const mockBackendResponse: ImportFileData[] = Array.from(
        { length: 15 },
        (_, i) => {
          const hasDriverId = i % 3 !== 0;
          const driverId = hasDriverId
            ? Math.floor(Math.random() * 100) + 1
            : null;

          const nameIndex = i % mockNames.length;
          const stateIndex = i % mockStates.length;

          return {
            id: `file-${i + 1}`,
            fileName: `driver_logs_${String(i + 1).padStart(3, "0")}.csv`,
            driverId,
            fileUrl: `https://example.com/temp/driver_logs_${i + 1}.csv`,
            suggestedDriverFullName:
              driverId === null ? mockNames[nameIndex] : undefined,
            suggestedLicenseNumber:
              driverId === null
                ? `DL${Math.floor(Math.random() * 900000 + 100000)}`
                : undefined,
            suggestedState:
              driverId === null ? mockStates[stateIndex] : undefined,
            hasEvents: driverId === null ? i % 2 === 0 : undefined,
          };
        }
      );

      setUploadedFiles(mockBackendResponse);

      const initialMappings: Record<string, ImportMapping> = {};
      mockBackendResponse.forEach((file) => {
        initialMappings[file.id] = {
          fileId: file.id,
          selectedDriverId: file.driverId || undefined,
          allowOverwrite: false,
        };
      });

      setMappings(initialMappings);
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [uploadedFiles.length]);

  const handleDriverChange = (fileId: string, driverId: number) => {
    setMappings((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        selectedDriverId: driverId,
      },
    }));
  };

  const handleOverwriteChange = (fileId: string, checked: boolean) => {
    setMappings((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        allowOverwrite: checked,
      },
    }));
  };

  const handleSubmit = async () => {
    // const submissionData = Object.values(mappings).map((mapping) => {
    //   const file = uploadedFiles.find((f) => f.id === mapping.fileId);
    //   return {
    //     fileName: file?.fileName,
    //     driverId: mapping.selectedDriverId,
    //     allowOverwrite: mapping.allowOverwrite,
    //     fileUrl: file?.fileUrl,
    //   };
    // });

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      notify(
        "Import started successfully. You will be notified when it's ready.",
        {
          type: "info",
          messageArgs: {},
        }
      );

      signalR.addMessage({
        type: SignalRTypeEnum.Import,
        status: SignalRStatusEnum.Success,
        importId: importId || "mock-import-id",
        machineName: "eld-file",
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 500);
      }
    } catch (error) {
      notify("Failed to start import. Please try again.", {
        type: "error",
        messageArgs: {},
      });

      signalR.addMessage({
        type: SignalRTypeEnum.Import,
        status: SignalRStatusEnum.Error,
      });
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading import data...
        </Typography>
      </div>
    );
  }

  const readyCount = Object.values(mappings).filter((m) => m.selectedDriverId)
    .length;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <CheckCircle className={classes.successIcon} />
          <div className={classes.headerText}>
            <Typography variant="body1" color="textPrimary">
              {uploadedFiles.length} files
            </Typography>
            <Typography variant="body1" color="textSecondary">
              •
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {readyCount} ready to import
            </Typography>
          </div>
        </div>
      </div>

      <div className={classes.filesList}>
        {uploadedFiles.map((file) => {
          const mapping = mappings[file.id];

          return (
            <div key={file.id} className={classes.fileCard}>
              <div className={classes.fileHeader}>
                <div className={classes.fileInfo}>
                  <FileIcon fontSize="small" color="action" />
                  <a
                    href={file.fileUrl}
                    className={classes.fileName}
                    title={file.fileName}
                  >
                    {file.fileName}
                  </a>
                </div>

                {file.hasEvents && (
                  <div className={classes.overwriteSection}>
                    <Tooltip
                      title="This driver already has events in this period. Allowing overwrite will replace existing driver events with the imported data."
                      placement="top"
                    >
                      <IconButton size="small">
                        <Warning
                          className={classes.warningIcon}
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                    <div className={classes.divider}>|</div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={mapping?.allowOverwrite || false}
                          onChange={(e) =>
                            handleOverwriteChange(file.id, e.target.checked)
                          }
                          size="small"
                          className={classes.checkbox}
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2">Overwrite</Typography>}
                    />
                  </div>
                )}
              </div>

              <div>
                {!file.driverId && (
                  <div className={classes.suggestedInfo}>
                    <Tooltip
                      title="This data is suggested from ELD file and it contains informations about name, license number and state."
                      placement="top"
                    >
                      <IconButton size="small">
                        <Warning
                          className={classes.warningIcon}
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" color="textSecondary">
                      {file.suggestedDriverFullName ?? "Unknown name"};{" "}
                      {file.suggestedLicenseNumber ?? "Unknown license number"};{" "}
                      {file.suggestedState ?? "Unknown state"}
                    </Typography>
                  </div>
                )}
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.driverSelect}
                >
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={mapping?.selectedDriverId || ""}
                    onChange={(e) =>
                      handleDriverChange(
                        file.id,
                        e.target.value ? Number(e.target.value) : 0
                      )
                    }
                    label="Driver"
                    disabled={isDriversLoading}
                  >
                    <MenuItem value="">
                      <em>Not imported</em>
                    </MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.driverName ||
                          driver.fullName ||
                          "Unknown Driver"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          );
        })}
      </div>

      <div className={classes.footer}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={readyCount === 0}
          startIcon={<CloudUpload />}
          className={classes.importButton}
        >
          Import {readyCount} Files
        </Button>
      </div>
    </div>
  );
};

export default ELDFileImportForm;
